import DropdownWithCheckboxes from "@components/shared/DropdownWithCheckboxes";
import LoginTextInput from "@/components/shared/LoginTextInput";
import TextInput from "@/components/shared/TextInput";
import { BASE_URL } from "@/utils/constants";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import SelectInput from "@/components/shared/SelectInput";
import { useNavigate } from "react-router-dom";

export default function Step3_LanguagesCaptcha({ resentCaptcha, values, errors, touched, setFieldValue, captchaData, captchaLoading, fetchCaptcha, token }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [targetLanguages, setTargetLanguages] = useState([]);
  const [publicKey, setPublicKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [genders, setGenders] = useState([
    { text: "MALE", id: 'M' },
    { text: "FEMALE", id: 'F' },
    { text: "OTHERS", id: 'O' },
  ]);

  useEffect(() => {
    getTargetLanguages();
    fetchKey();
  }, [dispatch]);

  useEffect(() => {
    if (resentCaptcha) {
      fetchCaptcha();
    }
  }, [resentCaptcha]);



  const fetchKey = async () => {
    try {
      const response = await fetch(`${BASE_URL}api/user/public-key`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      console.log("Public Key fetched:", data);
      if (data.data) {
        setPublicKey(data.data);
      } else {
        console.error("Failed to fetch public key");
      }
    } catch (err) {
      console.error("[fetchKey] Error:", err);
    }
  };


  const getTargetLanguages = async () => {
    try {
      setLoading(true);
      const response = await fetch(BASE_URL + "api/user/register/languages", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",

        }
      }, navigate);
      const data = await response.json();
      setTargetLanguages(data.data || []);
    } catch (error) {
      console.error("Failed to fetch target languages:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <TextInput
          label="College Enrollment Number"
          name="enrollmentNo"
          type="text"
          className="h-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
        />
        <SelectInput
          label="Gender"
          name="gender"
          className="h-10 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
        >
          <option value="">Select Gender</option>
          {genders?.length ? (
            genders.map((g) => (
              <option key={g.id} value={g.id}>
                {g.text}
              </option>
            ))
          ) : (
            <option value="" disabled>
              No genders available
            </option>
          )}
        </SelectInput>
        <TextInput
          label="Password"
          name="password"
          type={showPassword ? "text" : "password"}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
          endAdornment={
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
          showPasswordRules={true}
        />
        <TextInput
          label="Confirm Password"
          name="password2"
          type={showConfirmPassword ? "text" : "password"}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700 pl-10"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
          endAdornment={
            <button
              type="button"
              onClick={() => setShowConfirmPassword(prev => !prev)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />

        <DropdownWithCheckboxes
          label="Target Languages"
          options={targetLanguages.map((lang) => ({
            languageId: lang.languageId,
            name: lang.languageName,
          }))}
          values={values.targetLanguages}
          onChange={setFieldValue}
          name="targetLanguages"
          touched={touched.targetLanguages}
          errors={errors.targetLanguages}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] font-['Semantic/Strong'] text-gray-700"
          labelClassName="text-[#ec7026] font-['Semantic/Strong'] text-base"
          errorClassName="text-red-600 font-['Semantic/Strong'] text-sm"
          required={true}
        />
      </div>
      <div className="flex flex-col w-full mt-3">
        {captchaLoading ? (
          <div className="flex flex-col items-center justify-center w-40 bg-gray-100 rounded-lg border border-gray-200">
            <label
              className={`p-1 text-gray-700 text-[15px] font-bold`}
            >
              Captcha
            </label>
            <svg className="animate-spin h-8 w-8 text-gray-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        ) : (
          <div className="flex flex-col">
            <label
              className={`mt-2 text-gray-700 text-[15px] font-bold`}
            >
              Enter CAPTCHA

            </label>
            <div className="flex flex-row items-center gap-2 w-full">
              <img
                src={captchaData.captchaImage}
                alt="CAPTCHA"
                className="w-30 h-10 object-contain bg-gray-100 rounded-lg border border-gray-200"
              />
              <button
                type="button"
                onClick={fetchCaptcha}
                className="mt-2 text-sm text-[#ec7026] hover:text-[#d85d1a] font-['Semantic/Strong'] flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div className="">
                <LoginTextInput
                  labelClassName="text-[15px] font-bold"
                  name="captchaInput"
                  placeholder="Enter your captcha"
                  autoComplete="off"
                  type="text"
                  icon={
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}